/**
 * Sekmeleri sürükleyerek yeniden sıralama.
 *
 * Grid'deki kolon sürüklemesiyle aynı yaklaşım (bkz. useColumnReorder): dnd-kit yerine
 * doğrudan pointer olayları — tek eksende, bilinen kutularda bir liste için yeterli ve
 * ölçüm belirsizliği yok.
 *
 * Sekmeye özel olan kısım gruplar: Chrome'da bir sekmeyi grup bloğunun ortasına
 * bırakırsanız gruba katılır, kenarına bırakırsanız katılmaz. Burada aynı kural, bırakma
 * noktasının iki komşusuna bakılarak uygulanıyor — iki komşu da aynı gruptaysa sekme
 * grubun içine düşmüştür.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { TabModel } from '../model/types';

const DRAG_THRESHOLD_PX = 6;

export interface TabDragState {
  id: string;
  /** Bırakma çizgisinin ekran üzerindeki x'i; null ise geçerli hedef yok. */
  indicatorX: number | null;
  /** Çizginin dikey aralığı; şeritten ölçülür ki çizgi sayfanın tamamına uzanmasın. */
  indicatorTop: number;
  indicatorHeight: number;
}

/**
 * Bırakma noktasının hangi gruba düştüğünü söyler. `toIndex` kaynak HENÜZ çıkarılmamış
 * listeye göredir — store'daki moveTab de aynı sözleşmeyi bekliyor.
 */
export function dropTargetGroup(tabs: TabModel[], sourceId: string, toIndex: number): string | null {
  const without = tabs.filter((t) => t.tabId !== sourceId);
  const from = tabs.findIndex((t) => t.tabId === sourceId);
  const insertAt = toIndex > from ? toIndex - 1 : toIndex;
  const left = without[insertAt - 1];
  const right = without[insertAt];
  if (left?.groupId && left.groupId === right?.groupId) return left.groupId;
  return null;
}

export interface UseTabReorderArgs {
  tabs: TabModel[];
  onMove: (tabId: string, toIndex: number, groupId: string | null) => void;
}

export function useTabReorder({ tabs, onMove }: UseTabReorderArgs) {
  const [drag, setDrag] = useState<TabDragState | null>(null);
  const session = useRef<{ id: string; startX: number; started: boolean; targetIndex: number | null } | null>(
    null,
  );

  // Dinleyiciler sürükleme boyunca yaşar; donmuş bir sekme listesine takılmasınlar.
  const tabsRef = useRef(tabs);
  const onMoveRef = useRef(onMove);
  useEffect(() => {
    tabsRef.current = tabs;
    onMoveRef.current = onMove;
  }, [tabs, onMove]);

  const startDrag = useCallback((id: string, e: ReactPointerEvent<HTMLElement>) => {
    // Yalnızca birincil düğme: sağ tık menüsü ve orta tıkla kapatma etkilenmesin.
    if (e.button !== 0) return;
    const strip = (e.currentTarget as HTMLElement).closest('[role="tablist"]');
    if (!strip) return;

    const source = tabsRef.current.find((t) => t.tabId === id);
    // Şeridin kutusu sürükleme başında bir kez ölçülür; render sırasında ref okumak
    // gerekmesin diye çizginin dikey aralığı state ile taşınıyor.
    const stripBox = strip.getBoundingClientRect();
    const s = { id, startX: e.clientX, started: false, targetIndex: null as number | null };
    session.current = s;

    const move = (ev: PointerEvent) => {
      if (!s.started) {
        if (Math.abs(ev.clientX - s.startX) < DRAG_THRESHOLD_PX) return;
        s.started = true;
      }

      // Kutular her harekette okunuyor: şerit sürükleme sırasında kayabiliyor (taşma
      // oklarına yaklaşıldığında otomatik kaydırma) ve donmuş ölçüm hedefi kaydırıyor.
      let targetIndex: number | null = null;
      let indicatorX: number | null = null;
      for (const el of Array.from(strip.querySelectorAll<HTMLElement>('[data-tab-id]'))) {
        const tid = el.dataset['tabId'];
        if (!tid) continue;
        const box = el.getBoundingClientRect();
        if (ev.clientX < box.left || ev.clientX > box.right) continue;
        const idx = tabsRef.current.findIndex((t) => t.tabId === tid);
        if (idx < 0) continue;
        const beforeMid = ev.clientX < (box.left + box.right) / 2;
        targetIndex = beforeMid ? idx : idx + 1;
        indicatorX = beforeMid ? box.left : box.right;
        break;
      }

      // Sabit sekmeler her zaman başta durur: sabit olmayan bir sekme o bölgeye, sabit bir
      // sekme de dışına bırakılamaz. Sınırı burada uygulamazsak indikatör kullanıcıya
      // olmayacak bir yeri gösterir, bırakınca sekme başka yere gider.
      const pinnedCount = tabsRef.current.filter((t) => t.pinned).length;
      if (targetIndex !== null) {
        if (source?.pinned) targetIndex = Math.min(targetIndex, pinnedCount);
        else targetIndex = Math.max(targetIndex, pinnedCount);
      }

      s.targetIndex = targetIndex;
      setDrag({ id: s.id, indicatorX, indicatorTop: stripBox.top, indicatorHeight: stripBox.height });
    };

    const detach = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', onKey);
    };
    const finish = (commit: boolean) => {
      detach();
      session.current = null;
      setDrag(null);
      if (!s.started || !commit || s.targetIndex === null) return;
      const from = tabsRef.current.findIndex((t) => t.tabId === s.id);
      if (from < 0 || s.targetIndex === from || s.targetIndex === from + 1) return;
      onMoveRef.current(
        s.id,
        s.targetIndex,
        source?.pinned ? null : dropTargetGroup(tabsRef.current, s.id, s.targetIndex),
      );
    };
    const up = () => finish(true);
    const cancel = () => finish(false);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') finish(false);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', onKey);
  }, []);

  return { drag, startDrag };
}
